import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AccreditationSTEP, CovidTestSTEP, STEP, VaccinationSTEP } from "../../model/steps.model";
import { combineLatest } from "rxjs";
import {
  decrementStep,
  setBarcodeType,
  setPrimaryColor,
  setSecondaryColor,
  setTertiaryColor,
  setLanguage,
  incrementStep,
  setVerificationUrl,
  setSTEP,
  setFaceUrl,
  setBioSealUrl,
  setToken,
  setupUseCase,
  setPhotoDisplay,
} from "../../store/actions/global.action";
import { BioSealState } from "../../store/state/bioseal.state";
import { BioSealStateModel } from "../../model/bioseal.model";
import { Sandbox } from "../sandbox";
import { GlobalState } from "../../store/state/global.state";
import { FaceState } from "../../store/state/face.state";
import { USECASE } from "../../model/usecase.model";

@Injectable({
  providedIn: "root",
})
export class GlobalSandbox extends Sandbox {
  @Select(GlobalState.getTypeContent) contentType: Observable<boolean>;
  @Select(GlobalState.getBarcodeType) barcodeType: Observable<string>;
  @Select(BioSealState.getBioSealData) bioseal: Observable<BioSealStateModel>;
  @Select(FaceState.isLoaded) isLoaded: Observable<boolean>;
  @Select(GlobalState.getLanguage) lang: Observable<string>;
  @Select(BioSealState.isTimeOutBioSeal) timeoutBioSeal: Observable<boolean>;
  @Select(FaceState.faceTimeOut) timeoutFace: Observable<boolean>;
  @Select(GlobalState.getUseCase) useCase: Observable<USECASE>;
  @Select(GlobalState.getPhotoDisplay) photoDisplay: Observable<boolean>;
  @Select(GlobalState.isMobile) isMobile: Observable<boolean>;

  formsControl = false;
  idUploadControl = false;
  biometricsControl = false;


  constructor(private store: Store, public translate: TranslateService) {
    super(store);
  }

  configurateLanguage() {
    this.translate.addLangs(["generation-en", "generation-fr", "generation-es", "generation-pt"]);

    this.lang.subscribe((item) => {
      switch (item) {
        case "en":
          this.translate.use("generation-en");
          break;
        case "fr":
          this.translate.use("generation-fr");
          break;
        case "es":
          this.translate.use("generation-es");
          break;
        case "pt":
          this.translate.use("generation-pt");
          break;
        default:
          this.translate.getLangs().includes(navigator.language.substr(0, 2)) ? this.translate.setDefaultLang(navigator.language.substr(0, 2))
            : this.translate.setDefaultLang("generation-en");
          break;
      }
    });
  }

  setBarcodeType(barcodeType: string) {
    this.store.dispatch(new setBarcodeType(barcodeType));
  }
  setPrimaryColor(color) {
    this.store.dispatch(new setPrimaryColor(color));
  }
  setSecondaryColor(color) {
    this.store.dispatch(new setSecondaryColor(color));
  }
  setTertiaryColor(color) {
    this.store.dispatch(new setTertiaryColor(color));
  }
  setLanguage(lang) {
    this.store.dispatch(new setLanguage(lang));
  }
  setFaceUrl(url) {
    this.store.dispatch(new setFaceUrl(url));
  }
  setBioSealUrl(url) {
    this.store.dispatch(new setBioSealUrl(url));
  }
  setAuthorization(token) {
    this.store.dispatch(new setToken(token));
  }
  setVerification(url) {
    this.store.dispatch(new setVerificationUrl(url));
  }
  setPhotoDisplay(displaying: boolean) {
    this.store.dispatch(new setPhotoDisplay(displaying));
  }

  isUseCaseMenu(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.step === STEP.USE_CASE_MENU;
      })
    );
  }

  isVaccinationUseCase(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.useCase === USECASE.VACCINATION;
      })
    );
  }

  isGenericUseCase(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.useCase === USECASE.GENERIC;
      })
    );
  }

  isCovidTestUseCase(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.useCase === USECASE.COVID_TEST;
      })
    );
  }

  isAccreditationUseCase(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.useCase === USECASE.ACCREDITATION;
      })
    );
  }

  isConfigurationMenu(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.step === STEP.CONFIG_MENU && res.useCase === USECASE.GENERIC;
      })
    );
  }

  isUserForm(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.step === STEP.USER_FORM_PERSONAL;
      })
    );
  }
  isDocumentForm(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.step === STEP.USER_FORM_DOCUMENT;
      })
    );
  }
  isEventForm(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.step === AccreditationSTEP.USER_FORM_EVENT;
      })
    );
  }
  isFaceCapture(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        if (res.useCase === USECASE.GENERIC) {
          return res.step === STEP.FACE_CAPTURE;
        }
        if (res.useCase === USECASE.VACCINATION) {
          return res.step === VaccinationSTEP.FACE_CAPTURE;
        }
        if (res.useCase === USECASE.ACCREDITATION) {
          return res.step === AccreditationSTEP.FACE_CAPTURE;
        }
      })
    );
  }
  isDisplay(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        return res.useCase === USECASE.COVID_TEST ? res.step === CovidTestSTEP.BIOSEAL_DISPLAY : res.step === STEP.BIOSEAL_DISPLAY;
      })
    );
  }
  init() {
    this.store.dispatch(new setSTEP(STEP.USE_CASE_MENU));
  }
  increment() {
    this.store.dispatch(new incrementStep());
  }
  decrement() {
    this.store.dispatch(new decrementStep());
  }


  manageStepperControls(): void {

    combineLatest([this.useCase, this.global, this.isLoaded]).subscribe(
      ([resUseCase, resGlobal, resBiometrics]) => {
        if (resUseCase === USECASE.GENERIC) {
          switch (resGlobal.step) {
            case STEP.USER_FORM_PERSONAL:
              this.formsControl = false;
              break;

            case STEP.USER_FORM_DOCUMENT:
              this.formsControl = true;
              break;
            case STEP.FACE_CAPTURE:
              this.biometricsControl = resBiometrics
            default:
              break;
          }
        }
        if (resUseCase === USECASE.VACCINATION) {
          switch (resGlobal.step) {
            case VaccinationSTEP.USER_FORM_PERSONAL:
              this.formsControl = false;
              this.biometricsControl = false;
              break;

            case VaccinationSTEP.USER_FORM_PRACTITIONER:
              this.formsControl = false;
              this.biometricsControl = false;
              break;

            case VaccinationSTEP.USER_FORM_DOCUMENT:
              this.formsControl = true;
              this.biometricsControl = false;
              break;

            case VaccinationSTEP.FACE_CAPTURE:
              this.formsControl = true;
              this.biometricsControl = true;
              break;

            default:
              break;
          }
        }
        if (resUseCase === USECASE.COVID_TEST) {
          switch (resGlobal.step) {

            case CovidTestSTEP.USER_FORM_PERSONAL:
              this.formsControl = false;
              break;

            case CovidTestSTEP.USER_FORM_TEST:
              this.formsControl = true;
              break;
            default:
              break;
          }
        }
        if (resUseCase === USECASE.ACCREDITATION) {
          switch (resGlobal.step) {

            case AccreditationSTEP.IDENTITY_DOCUMENT:
              this.idUploadControl = true;
              this.biometricsControl = false;
              this.formsControl = false;
              break;

            case AccreditationSTEP.FACE_CAPTURE:
              this.idUploadControl = true;
              this.biometricsControl = true;
              this.formsControl = false;
              break;

            case AccreditationSTEP.USER_FORM_PERSONAL:
              this.idUploadControl = true;
              this.biometricsControl = true;
              this.formsControl = false;
              break;

            case AccreditationSTEP.USER_FORM_EVENT:
              this.idUploadControl = true;
              this.biometricsControl = true;
              this.formsControl = true;
              break;

            default:
              break;
          }
        }
      }
    );

  }

  isBarcodeBioSeal(): Observable<boolean> {
    return this.barcodeType.pipe(
      map((res) => {
        return res == "BioSeal";
      })
    );
  }

  isBarcodeQR(): Observable<boolean> {
    return this.barcodeType.pipe(
      map((res) => {
        return res == "QR";
      })
    );
  }

  isBarcodeDataMatrix(): Observable<boolean> {
    return this.barcodeType.pipe(
      map((res) => {
        return res == "DataMatrix";
      })
    );
  }

  isLeftEnabled(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        switch (res.step) {
          case STEP.USE_CASE_MENU:
            return false;
          default:
            return true;
        }
      })
    );
  }
  setUseCase(usecase) {
    this.store.dispatch(new setupUseCase(usecase));
  }

  isCommunicationEstablished(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        if (
          res.faceUrl == null ||
          res.biosealUrl == null ||
          res.token == null
        ) {
          return false;
        } else {
          return true;
        }
      })
    );
  }

  isIdUpload(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        if (res.useCase === USECASE.ACCREDITATION) {
          return res.step === AccreditationSTEP.IDENTITY_DOCUMENT;
        }
        else {
          return false;
        }
      })
    )
  }

  isBiometrics(): Observable<boolean> {
    return this.contentType.pipe(
      map((res) => {
        return res;
      })
    );
  }
  isBioSealDisplay(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        if (res.useCase === USECASE.GENERIC) {
          return res.step === STEP.BIOSEAL_DISPLAY;
        }
        if (res.useCase === USECASE.VACCINATION) {
          return res.step === VaccinationSTEP.BIOSEAL_DISPLAY;
        }
        if (res.useCase === USECASE.COVID_TEST) {
          return res.step === CovidTestSTEP.BIOSEAL_DISPLAY;
        }
      })
    );
  }
  isUserFormDone(): Observable<boolean> {
    return combineLatest([this.global, this.bioseal]).pipe(
      map((res) => {
        if (res[0].useCase === USECASE.GENERIC) {
          return res[1].isDocumentDataLoaded && res[1].isPersonalDataLoaded;
        }
        if (res[0].useCase === USECASE.VACCINATION) {
          return (
            res[1].isDocumentDataLoaded &&
            res[1].isPersonalDataLoaded &&
            res[1].isPractitionerDataLoaded
          );
        }
        if (res[0].useCase === USECASE.COVID_TEST) {
          return res[1].isPersonalDataLoaded && res[1].isTestDataLoaded;
        }
      })
    );
  }
  isFaceMenu(): Observable<boolean> {
    return this.global.pipe(
      map((res) => {
        if (res.useCase === USECASE.GENERIC) {
          return res.step === STEP.FACE_CAPTURE;
        }
        if (res.useCase === USECASE.VACCINATION) {
          return res.step === VaccinationSTEP.FACE_CAPTURE;
        }
      })
    );
  }
}
